import { useQuery } from '@tanstack/react-query';
import { kioskService } from '@/api/admin/services/kiosk.service';
import { rateService } from '@/api/admin/services/rate.service';
import { transactionService } from '@/api/admin/services/transaction.service';
import { vendorService } from '@/api/admin/services/vendor.service';

/** How many of the newest payments the vendor page loads. */
export const TRANSACTION_LIMIT = 200;

// Keys start with the same prefixes the list pages use, so saving a vendor/kiosk/rate elsewhere refreshes these too.
export function useVendor(vendorId: number) {
  return useQuery({
    queryKey: ['admin', 'vendors', 'detail', vendorId],
    queryFn: () => vendorService.getById(vendorId),
    enabled: Number.isFinite(vendorId),
  });
}

export function useVendorKiosks(vendorId: number) {
  return useQuery({
    queryKey: ['admin', 'kiosks', 'vendor', vendorId],
    queryFn: () => kioskService.list(vendorId),
    enabled: Number.isFinite(vendorId),
  });
}

export function useVendorRates(vendorId: number) {
  return useQuery({
    queryKey: ['admin', 'rate-charges', 'vendor', vendorId],
    queryFn: () => rateService.list(vendorId),
    enabled: Number.isFinite(vendorId),
  });
}

/** Empty `from` / `to` means no limit on that side. Dates are YYYY-MM-DD. */
export interface DateRange {
  from: string;
  to: string;
}

export function useVendorTransactions(vendorId: number, range: DateRange) {
  return useQuery({
    queryKey: ['admin', 'transactions', 'vendor', vendorId, range.from, range.to],
    queryFn: () =>
      transactionService.list({
        vendor: vendorId,
        from: range.from || undefined,
        to: range.to || undefined,
        limit: TRANSACTION_LIMIT,
      }),
    enabled: Number.isFinite(vendorId),
    placeholderData: (previous) => previous, // keep the old rows on screen while a new range loads
  });
}
